'use client';

import { CalendarIcon } from '@/icons';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from 'ui/button';
import { Calendar } from 'ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';
import { useFormField } from '.';
import { FormField } from './Field';
import { UseFormFieldProps } from './type';

interface Props extends UseFormFieldProps {
  name: string;
  placeholder?: string;
  disableFuture?: boolean;
  require18Years?: boolean;
  className?: string;
  formDate?: boolean;
  icon?: React.ReactNode;
  startMonth?: Date;
  endMonth?: Date;
}

const toValidDate = (value: unknown): Date | undefined => {
  if (!value && value !== 0) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  let date = new Date(value as string | number);
  if (Number.isNaN(date.getTime()) && typeof value === 'string') {
    const jpMatch = value.match(/(\d{4})年\s*(\d{1,2})月\s*(\d{1,2})日/);
    if (jpMatch) {
      const year = Number(jpMatch[1]);
      const month = Number(jpMatch[2]) - 1;
      const day = Number(jpMatch[3]);
      date = new Date(year, month, day);
    }
  }
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const DatePicker = ({
  name,
  placeholder = 'Pick a date',
  disableFuture = false,
  require18Years = false,
  className,
  formDate = false,
  startMonth,
  endMonth,
  icon,
  ...props
}: Props) => {
  const { register, setValue, watch } = useFormContext();
  const { formFieldProps, error } = useFormField({ name, ...props });
  const formValue = watch(name) as string | undefined;
  const [selected, setSelected] = useState<Date | undefined>(() => toValidDate(formValue));
  useEffect(() => {
    setSelected(toValidDate(formValue));
  }, [formValue]);

  const handleChange = (date?: Date) => {
    const nextDate = toValidDate(date);
    setSelected(nextDate);
    setValue(name, nextDate?.toISOString() ?? '', { shouldDirty: true });
  };

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  return (
    <FormField {...formFieldProps}>
      {/* Hidden input to be registered */}
      <input type="hidden" {...register(name)} value={selected?.toISOString() ?? ''} />
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            type="button"
            className={cn(
              'group w-full justify-between bg-card px-3 font-medium border border-surface-hover outline-offset-0 focus-visible:border-ring focus-visible:outline-[3px] focus-visible:outline-ring/20',
              className,
              error && 'border-destructive focus-visible:ring-destructive',
            )}
          >
            <span className={!selected && formDate ? 'text-muted-foreground' : 'text-foreground'}>
              {selected ? format(selected, 'y/L/dd') : placeholder}
            </span>

            {icon ? (
              icon
            ) : (
              <CalendarIcon
                size={16}
                strokeWidth={2}
                className="shrink-0 text-muted-foreground/80 transition-colors group-hover:text-foreground"
                aria-hidden="true"
              />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-2" align="center">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={handleChange}
            initialFocus
            startMonth={startMonth}
            endMonth={endMonth}
            captionLayout="dropdown"
            defaultMonth={require18Years ? maxDate : new Date()}
            classNames={{
              day_selected: 'bg-primary text-primary-foreground hover:bg-primary/90',

              day_today: 'border border-primary',
            }}
            disabled={disableFuture ? { after: yesterday } : require18Years ? { after: maxDate } : false}
          />
        </PopoverContent>
      </Popover>
    </FormField>
  );
};

export default DatePicker;
